import { subMonths } from 'date-fns';
import { RoleType } from '../types/user/accounts/RoleType';
import { AccountType } from '../types/account/AccountType';
import { t } from 'i18next';
import { AccountResponse } from '../models/account/AccountResponse';
import { HTTP_HTTPS_REGEX } from './regex';
import { MonitorType } from '../types/monitor/MonitorType';

export const MINUS = '-';

export const SELECTED_BRAND_COLOR = '#53CBA2';
export const OTHERS_COLOR = '#C7C4C4';

export const COLORS_BARS = ['#0a2073', '#912b2b', '#11898c'];

export const COLORS_FIRST_SERIES = [
  '#009efd',
  '#8CDCC1',
  '#1B5FF8',
  '#303030',
  '#6794FA',
  '#B3C9FC',
  '#959596',
  '#00a189',
  '#008b8a',
  '#007481',
  '#225d70',
  '#4b789a',
  '#1c98b3',
  '#00b7b7',
  '#4cd3a5',
  '#a0ea88',
  '#09522a',
];

export const COLORS_SECOND_SERIES = [
  '#4b789a',
  '#1c98b3',
  '#00b7b7',
  '#22634b',
  '#a0ea88',
  '#09522a',
  '#268120',
  '#007947',
  '#006f60',
  '#00636b',
  '#005567',
];

export const isAdminOrOwner = (role: RoleType): boolean => {
  return ['ADMIN', 'OWNER'].includes(role);
};

export const isValidDate = (date: string): boolean => {
  return !isNaN(Date.parse(date));
};

export const round = (num: number, fractionDigits: number): number => {
  return Number(num.toFixed(fractionDigits));
};

export const getInitials = (word: string, alternativeWord: string): string => {
  let sentence = word;
  if (word.trim().length === 0) {
    sentence = alternativeWord;
  }

  const words = sentence.trim().split(' ');
  const initials = words.map((word) => {
    return word.length > 0 ? word[0].toUpperCase() : '';
  });
  return initials.join('');
};

export const normalizeValue = (value: number): number => {
  if (value === 0) {
    return 0;
  }

  if (Number.isInteger(value)) {
    return value;
  }

  return Number(value.toFixed(2));
};

export const getDefaultRange = (withCurrentDay = true): Date[] => {
  const today = new Date();
  const from = subMonths(today, 1);
  return [from, today];
};

export const numberFormat = (): Intl.NumberFormat => {
  return new Intl.NumberFormat('pl-PL', {
    useGrouping: true,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

/**
 * Browser-locale count formatter used by BotsActivity tiles, breakdown rows, and platform
 * cards. Returns an em-dash for null/undefined/non-finite so empty cells stay readable.
 *
 * Uses `new Intl.NumberFormat()` with no locale arg (i.e. the user's browser locale), matching
 * the original inline behavior. The PL-locale alternative is `numberFormat().format(n)` —
 * pick that one when the surrounding module is already locale-pinned, otherwise prefer this
 * helper so consumers stay consistent.
 */
export const formatCount = (n: number | null | undefined): string => {
  if (n == null || !Number.isFinite(n)) return '—';
  return new Intl.NumberFormat().format(n);
};

/**
 * Formats a 0..1 fraction as a percent string (e.g. 0.073 → "7.3%"). Returns em-dash for
 * null/undefined/non-finite. `digits` controls fraction digits in the output.
 */
export const formatPercent = (
  fraction: number | null | undefined,
  digits = 1
): string => {
  if (fraction == null || !Number.isFinite(fraction)) return '—';
  return `${(fraction * 100).toFixed(digits)}%`;
};

export const getTrialDays = (accountResponse: AccountResponse): number => {
  if (!accountResponse) {
    return 0;
  }

  const trialPeriodDays = accountResponse.trialDays;
  const currentDate = new Date();
  const createdDate = new Date(accountResponse.created);
  const elapsedMilliseconds = currentDate.getTime() - createdDate.getTime();
  const elapsedDays = Math.floor(elapsedMilliseconds / (1000 * 60 * 60 * 24));
  const trialDays = trialPeriodDays - elapsedDays;

  if (trialDays > 0) {
    return trialDays;
  }

  return 0;
};

export const getRandomColor = (): string => {
  const letters = '0123456789ABCDEF';
  let color = '#';
  for (let i = 0; i < 6; i++) {
    color += letters[Math.floor(Math.random() * 16)];
  }

  return color;
};

export const formatLargeNumber = (value: number | null): string => {
  if (!value) {
    return '-';
  }

  if (value >= 1_000_000_000) {
    return (value / 1_000_000_000).toFixed(0) + 'B';
  } else if (value >= 1_000_000) {
    return (value / 1_000_000).toFixed(0) + 'M';
  } else if (value >= 1_000) {
    return (value / 1_000).toFixed(0) + 'K';
  } else {
    return value.toString();
  }
};

export const getTextForVisibility = (percent: number): string => {
  if (percent === 0) {
    return 'notVisible';
  }
  if (percent > 0 && percent <= 2) {
    return 'minimal';
  }

  if (percent > 2 && percent <= 9) {
    return 'emerging';
  }

  if (percent > 9 && percent <= 29) {
    return 'decent';
  }

  if (percent > 29 && percent <= 49) {
    return 'respectable';
  }

  if (percent > 49 && percent <= 69) {
    return 'good';
  }

  return 'excellent';
};

export const getTrendValue = (value: number, fixed?: number): string | number => {
  if (value === 0) {
    return 'stable';
  }

  if (fixed) {
    return value.toFixed(fixed);
  }

  return value;
};

export const getStatsIcon = (value: string): string => {
  const val = Number(value);

  if (isNaN(val)) {
    return 'ico--dot';
  }

  return val > 0 ? 'ico--chart-up' : 'ico--chart-down';
};

export const normalizeKeyword = (value: string) => {
  return value
    .trim()
    .replace(/[‘’]/g, "'") // Apostrofy → '
    .replace(/[“”„]/g, '"') // Cudzysłowy → "
    .replace(/—|–/g, '-') // Myślniki → "-"
    .replace(/\u00A0/g, ' ') // Twarda spacja → normalna spacja
    .replace(/\s+/g, ' ');
};

export const downloadBlobAsFile = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();

  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
};

export const accountTypeToText = (accountType: AccountType) => {
  if (accountType === 'TRIAL') {
    return 'Trial';
  }

  if (['ADVANCED', 'ADVANCED_V2', 'ADVANCED_V3'].includes(accountType)) {
    return 'Advanced';
  }

  if (['ESSENTIAL', 'ESSENTIAL_V3'].includes(accountType)) {
    return 'Essential';
  }

  if (['BUSINESS', 'BUSINESS_V3'].includes(accountType)) {
    return 'Business';
  }

  if (accountType === 'PRO') {
    return 'Pro';
  }

  if (accountType === 'STANDARD') {
    return 'Standard';
  }

  if (['ENTERPRISE', 'ENTERPRISE_V3'].includes(accountType)) {
    return 'Enterprise';
  }

  return '';
};

/**
 * Normalizes a project URL entered by the user.
 *
 * Rules:
 * - Preserves the first path segment if it looks like a language/region code (2-4 letters,
 *   optionally followed by a hyphen and 2-4 letters, e.g. "de", "en-us", "de-DE", "zh-hant").
 * - Strips everything after the first path segment (deeper paths, query strings, fragments).
 * - Removes trailing slashes.
 * - Trims surrounding whitespace.
 * - If the input has no protocol, assumes https://.
 *
 * Examples:
 *   https://www.scania.com/de          → https://www.scania.com/de
 *   https://www.scania.com/de/de/home  → https://www.scania.com/de
 *   https://www.amazon.pl/?tag=xxx     → https://www.amazon.pl
 *   https://www.amazon.pl/products/x   → https://www.amazon.pl
 *   www.example.com                    → https://www.example.com
 */
export const cleanDomain = (url: string): string => {
  const trimmed = url.trim();

  // Ensure there is a protocol so URL() can parse it.
  const withProtocol = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;

  let parsed: URL;
  try {
    parsed = new URL(withProtocol);
  } catch {
    // Unparseable — return as-is with whitespace removed.
    return trimmed;
  }

  const base = `${parsed.protocol}//${parsed.host}`;

  // Extract the first path segment (ignoring the leading "/").
  const firstSegment = parsed.pathname.split('/').filter(Boolean)[0] ?? '';

  // A language/region code: 2-4 ASCII letters, optionally "-" + 2-4 ASCII letters.
  // Case-insensitive (e.g. "de", "en-US", "zh-hant").
  const LANG_REGION_REGEX = /^[a-zA-Z]{2,4}(-[a-zA-Z]{2,4})?$/;

  if (firstSegment && LANG_REGION_REGEX.test(firstSegment)) {
    return `${base}/${firstSegment}`;
  }

  return base;
};

export const isContainText = (text: string | null, compare: string): boolean => {
  return !!text && new RegExp(`\\b${text.toLowerCase().trim()}\\b`, 'i').test(compare.toLowerCase());
};

export const scrollToHash = (hash: string) => {
  if (hash) {
    const element = document.getElementById(hash.substring(1));
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  }
};

export const countryOptions: { name: string; code: string }[] = [
  { name: 'Andorra', code: 'AD' },
  { name: 'United Arab Emirates', code: 'AE' },
  { name: 'Antigua and Barbuda', code: 'AG' },
  { name: 'Anguilla', code: 'AI' },
  { name: 'Albania', code: 'AL' },
  { name: 'Armenia', code: 'AM' },
  { name: 'Angola', code: 'AO' },
  { name: 'Argentina', code: 'AR' },
  { name: 'American Samoa', code: 'AS' },
  { name: 'Austria', code: 'AT' },
  { name: 'Australia', code: 'AU' },
  { name: 'Aruba', code: 'AW' },
  { name: 'Åland Islands', code: 'AX' },
  { name: 'Azerbaijan', code: 'AZ' },
  { name: 'Bosnia and Herzegovina', code: 'BA' },
  { name: 'Barbados', code: 'BB' },
  { name: 'Bangladesh', code: 'BD' },
  { name: 'Belgium', code: 'BE' },
  { name: 'Burkina Faso', code: 'BF' },
  { name: 'Bulgaria', code: 'BG' },
  { name: 'Bahrain', code: 'BH' },
  { name: 'Burundi', code: 'BI' },
  { name: 'Benin', code: 'BJ' },
  { name: 'Saint Barthélemy', code: 'BL' },
  { name: 'Bermuda', code: 'BM' },
  { name: 'Brunei Darussalam', code: 'BN' },
  { name: 'Bolivia', code: 'BO' },
  { name: 'Bonaire, Sint Eustatius and Saba', code: 'BQ' },
  { name: 'Brazil', code: 'BR' },
  { name: 'Bahamas', code: 'BS' },
  { name: 'Bhutan', code: 'BT' },
  { name: 'Bouvet Island', code: 'BV' },
  { name: 'Botswana', code: 'BW' },
  { name: 'Belize', code: 'BZ' },
  { name: 'Canada', code: 'CA' },
  { name: 'Cocos (Keeling) Islands', code: 'CC' },
  { name: 'Congo', code: 'CG' },
  { name: 'Switzerland', code: 'CH' },
  { name: "Côte d'Ivoire", code: 'CI' },
  { name: 'Cook Islands', code: 'CK' },
  { name: 'Chile', code: 'CL' },
  { name: 'Cameroon', code: 'CM' },
  { name: 'China', code: 'CN' },
  { name: 'Colombia', code: 'CO' },
  { name: 'Costa Rica', code: 'CR' },
  { name: 'Cabo Verde', code: 'CV' },
  { name: 'Curaçao', code: 'CW' },
  { name: 'Christmas Island', code: 'CX' },
  { name: 'Cyprus', code: 'CY' },
  { name: 'Czechia', code: 'CZ' },
  { name: 'Germany', code: 'DE' },
  { name: 'Djibouti', code: 'DJ' },
  { name: 'Denmark', code: 'DK' },
  { name: 'Dominica', code: 'DM' },
  { name: 'Dominican Republic', code: 'DO' },
  { name: 'Algeria', code: 'DZ' },
  { name: 'Ecuador', code: 'EC' },
  { name: 'Estonia', code: 'EE' },
  { name: 'Egypt', code: 'EG' },
  { name: 'Western Sahara', code: 'EH' },
  { name: 'Eritrea', code: 'ER' },
  { name: 'Spain', code: 'ES' },
  { name: 'Ethiopia', code: 'ET' },
  { name: 'Finland', code: 'FI' },
  { name: 'Fiji', code: 'FJ' },
  { name: 'Falkland Islands', code: 'FK' },
  { name: 'Micronesia', code: 'FM' },
  { name: 'Faroe Islands', code: 'FO' },
  { name: 'France', code: 'FR' },
  { name: 'Gabon', code: 'GA' },
  { name: 'United Kingdom', code: 'GB' },
  { name: 'Grenada', code: 'GD' },
  { name: 'Georgia', code: 'GE' },
  { name: 'French Guiana', code: 'GF' },
  { name: 'Guernsey', code: 'GG' },
  { name: 'Ghana', code: 'GH' },
  { name: 'Gibraltar', code: 'GI' },
  { name: 'Greenland', code: 'GL' },
  { name: 'Gambia', code: 'GM' },
  { name: 'Guinea', code: 'GN' },
  { name: 'Guadeloupe', code: 'GP' },
  { name: 'Equatorial Guinea', code: 'GQ' },
  { name: 'Greece', code: 'GR' },
  { name: 'South Georgia and the South Sandwich Islands', code: 'GS' },
  { name: 'Guatemala', code: 'GT' },
  { name: 'Guam', code: 'GU' },
  { name: 'Guinea-Bissau', code: 'GW' },
  { name: 'Guyana', code: 'GY' },
  { name: 'Hong Kong', code: 'HK' },
  { name: 'Heard Island and McDonald Islands', code: 'HM' },
  { name: 'Honduras', code: 'HN' },
  { name: 'Croatia', code: 'HR' },
  { name: 'Hungary', code: 'HU' },
  { name: 'Indonesia', code: 'ID' },
  { name: 'Ireland', code: 'IE' },
  { name: 'Israel', code: 'IL' },
  { name: 'Isle of Man', code: 'IM' },
  { name: 'India', code: 'IN' },
  { name: 'British Indian Ocean Territory', code: 'IO' },
  { name: 'Iraq', code: 'IQ' },
  { name: 'Iceland', code: 'IS' },
  { name: 'Italy', code: 'IT' },
  { name: 'Jersey', code: 'JE' },
  { name: 'Jamaica', code: 'JM' },
  { name: 'Jordan', code: 'JO' },
  { name: 'Japan', code: 'JP' },
  { name: 'Kenya', code: 'KE' },
  { name: 'Kyrgyzstan', code: 'KG' },
  { name: 'Cambodia', code: 'KH' },
  { name: 'Kiribati', code: 'KI' },
  { name: 'Comoros', code: 'KM' },
  { name: 'Saint Kitts and Nevis', code: 'KN' },
  { name: 'South Korea', code: 'KR' },
  { name: 'Kuwait', code: 'KW' },
  { name: 'Cayman Islands', code: 'KY' },
  { name: 'Kazakhstan', code: 'KZ' },
  { name: "Lao People's Democratic Republic", code: 'LA' },
  { name: 'Lebanon', code: 'LB' },
  { name: 'Saint Lucia', code: 'LC' },
  { name: 'Liechtenstein', code: 'LI' },
  { name: 'Sri Lanka', code: 'LK' },
  { name: 'Liberia', code: 'LR' },
  { name: 'Lesotho', code: 'LS' },
  { name: 'Lithuania', code: 'LT' },
  { name: 'Luxembourg', code: 'LU' },
  { name: 'Latvia', code: 'LV' },
  { name: 'Morocco', code: 'MA' },
  { name: 'Monaco', code: 'MC' },
  { name: 'Moldova', code: 'MD' },
  { name: 'Montenegro', code: 'ME' },
  { name: 'Saint Martin (French part)', code: 'MF' },
  { name: 'Madagascar', code: 'MG' },
  { name: 'Marshall Islands', code: 'MH' },
  { name: 'North Macedonia', code: 'MK' },
  { name: 'Mongolia', code: 'MN' },
  { name: 'Macao', code: 'MO' },
  { name: 'Northern Mariana Islands', code: 'MP' },
  { name: 'Martinique', code: 'MQ' },
  { name: 'Mauritania', code: 'MR' },
  { name: 'Montserrat', code: 'MS' },
  { name: 'Malta', code: 'MT' },
  { name: 'Mauritius', code: 'MU' },
  { name: 'Maldives', code: 'MV' },
  { name: 'Malawi', code: 'MW' },
  { name: 'Mexico', code: 'MX' },
  { name: 'Malaysia', code: 'MY' },
  { name: 'Mozambique', code: 'MZ' },
  { name: 'Namibia', code: 'NA' },
  { name: 'New Caledonia', code: 'NC' },
  { name: 'Niger', code: 'NE' },
  { name: 'Norfolk Island', code: 'NF' },
  { name: 'Nigeria', code: 'NG' },
  { name: 'Netherlands', code: 'NL' },
  { name: 'Norway', code: 'NO' },
  { name: 'Nepal', code: 'NP' },
  { name: 'Nauru', code: 'NR' },
  { name: 'Niue', code: 'NU' },
  { name: 'New Zealand', code: 'NZ' },
  { name: 'Oman', code: 'OM' },
  { name: 'Panama', code: 'PA' },
  { name: 'Peru', code: 'PE' },
  { name: 'French Polynesia', code: 'PF' },
  { name: 'Papua New Guinea', code: 'PG' },
  { name: 'Philippines', code: 'PH' },
  { name: 'Pakistan', code: 'PK' },
  { name: 'Poland', code: 'PL' },
  { name: 'Saint Pierre and Miquelon', code: 'PM' },
  { name: 'Pitcairn', code: 'PN' },
  { name: 'Puerto Rico', code: 'PR' },
  { name: 'Palestine, State of', code: 'PS' },
  { name: 'Portugal', code: 'PT' },
  { name: 'Palau', code: 'PW' },
  { name: 'Paraguay', code: 'PY' },
  { name: 'Qatar', code: 'QA' },
  { name: 'Réunion', code: 'RE' },
  { name: 'Romania', code: 'RO' },
  { name: 'Serbia', code: 'RS' },
  { name: 'Rwanda', code: 'RW' },
  { name: 'Saudi Arabia', code: 'SA' },
  { name: 'Solomon Islands', code: 'SB' },
  { name: 'Seychelles', code: 'SC' },
  { name: 'Sweden', code: 'SE' },
  { name: 'Singapore', code: 'SG' },
  { name: 'Saint Helena, Ascension and Tristan da Cunha', code: 'SH' },
  { name: 'Slovenia', code: 'SI' },
  { name: 'Svalbard and Jan Mayen', code: 'SJ' },
  { name: 'Slovakia', code: 'SK' },
  { name: 'Sierra Leone', code: 'SL' },
  { name: 'San Marino', code: 'SM' },
  { name: 'Senegal', code: 'SN' },
  { name: 'Suriname', code: 'SR' },
  { name: 'Sao Tome and Principe', code: 'ST' },
  { name: 'El Salvador', code: 'SV' },
  { name: 'Sint Maarten (Dutch part)', code: 'SX' },
  { name: 'Eswatini', code: 'SZ' },
  { name: 'Turks and Caicos Islands', code: 'TC' },
  { name: 'Chad', code: 'TD' },
  { name: 'French Southern Territories', code: 'TF' },
  { name: 'Togo', code: 'TG' },
  { name: 'Thailand', code: 'TH' },
  { name: 'Tajikistan', code: 'TJ' },
  { name: 'Tokelau', code: 'TK' },
  { name: 'Timor-Leste', code: 'TL' },
  { name: 'Turkmenistan', code: 'TM' },
  { name: 'Tunisia', code: 'TN' },
  { name: 'Tonga', code: 'TO' },
  { name: 'Türkiye', code: 'TR' },
  { name: 'Trinidad and Tobago', code: 'TT' },
  { name: 'Tuvalu', code: 'TV' },
  { name: 'Taiwan', code: 'TW' },
  { name: 'Tanzania, United Republic of', code: 'TZ' },
  { name: 'Ukraine', code: 'UA' },
  { name: 'Uganda', code: 'UG' },
  { name: 'United States Minor Outlying Islands', code: 'UM' },
  { name: 'United States of America', code: 'US' },
  { name: 'Uruguay', code: 'UY' },
  { name: 'Uzbekistan', code: 'UZ' },
  { name: 'Holy See', code: 'VA' },
  { name: 'Saint Vincent and the Grenadines', code: 'VC' },
  { name: 'Virgin Islands (British)', code: 'VG' },
  { name: 'Virgin Islands (U.S.)', code: 'VI' },
  { name: 'Viet Nam', code: 'VN' },
  { name: 'Vanuatu', code: 'VU' },
  { name: 'Wallis and Futuna', code: 'WF' },
  { name: 'Samoa', code: 'WS' },
  { name: 'Kosovo', code: 'XK' },
  { name: 'Mayotte', code: 'YT' },
  { name: 'South Africa', code: 'ZA' },
  { name: 'Zambia', code: 'ZM' },
];

export const toLocalDate = (utcString: string) => {
  const date = new Date(utcString);
  return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
};

export const removeHttpHttps = (url: string): string => {
  return url.replace(HTTP_HTTPS_REGEX, '');
};

export const extractDomain = (fullUrl: string): string => {
  try {
    const url = new URL(fullUrl);
    const host = url.hostname.replace(/^www\./, '');
    return `${url.protocol}//${host}`;
  } catch {
    return '';
  }
};

export const brightDataCountries = [
  { name: 'Global', code: null, icon: 'ico--globe' },
  { name: 'Afghanistan', code: 'AF', icon: 'fi-af' },
  { name: 'Albania', code: 'AL', icon: 'fi-al' },
  { name: 'Algeria', code: 'DZ', icon: 'fi-dz' },
  { name: 'Andorra', code: 'AD', icon: 'fi-ad' },
  { name: 'Angola', code: 'AO', icon: 'fi-ao' },
  { name: 'Antigua And Barbuda', code: 'AG', icon: 'fi-ag' },
  { name: 'Argentina', code: 'AR', icon: 'fi-ar' },
  { name: 'Armenia', code: 'AM', icon: 'fi-am' },
  { name: 'Australia', code: 'AU', icon: 'fi-au' },
  { name: 'Austria', code: 'AT', icon: 'fi-at' },
  { name: 'Azerbaijan', code: 'AZ', icon: 'fi-az' },
  { name: 'Bahamas', code: 'BS', icon: 'fi-bs' },
  { name: 'Bahrain', code: 'BH', icon: 'fi-bh' },
  { name: 'Bangladesh', code: 'BD', icon: 'fi-bd' },
  { name: 'Barbados', code: 'BB', icon: 'fi-bb' },
  { name: 'Belgium', code: 'BE', icon: 'fi-be' },
  { name: 'Belize', code: 'BZ', icon: 'fi-bz' },
  { name: 'Benin', code: 'BJ', icon: 'fi-bj' },
  { name: 'Bhutan', code: 'BT', icon: 'fi-bt' },
  { name: 'Bolivia', code: 'BO', icon: 'fi-bo' },
  { name: 'Bosnia And Herzegovina', code: 'BA', icon: 'fi-ba' },
  { name: 'Botswana', code: 'BW', icon: 'fi-bw' },
  { name: 'Brazil', code: 'BR', icon: 'fi-br' },
  { name: 'Brunei', code: 'BN', icon: 'fi-bn' },
  { name: 'Bulgaria', code: 'BG', icon: 'fi-bg' },
  { name: 'Burkina Faso', code: 'BF', icon: 'fi-bf' },
  { name: 'Burundi', code: 'BI', icon: 'fi-bi' },
  { name: 'Cambodia', code: 'KH', icon: 'fi-kh' },
  { name: 'Cameroon', code: 'CM', icon: 'fi-cm' },
  { name: 'Canada', code: 'CA', icon: 'fi-ca' },
  { name: 'Cape Verde', code: 'CV', icon: 'fi-cv' },
  { name: 'Central African Republic', code: 'CF', icon: 'fi-cf' },
  { name: 'Chad', code: 'TD', icon: 'fi-td' },
  { name: 'Chile', code: 'CL', icon: 'fi-cl' },
  { name: 'Colombia', code: 'CO', icon: 'fi-co' },
  { name: 'Comoros', code: 'KM', icon: 'fi-km' },
  { name: 'Congo', code: 'CG', icon: 'fi-cg' },
  { name: 'Costa Rica', code: 'CR', icon: 'fi-cr' },
  { name: "Cote D'Ivoire (Ivory Coast)", code: 'CI', icon: 'fi-ci' },
  { name: 'Croatia (Hrvatska)', code: 'HR', icon: 'fi-hr' },
  { name: 'Cyprus', code: 'CY', icon: 'fi-cy' },
  { name: 'Czech Republic', code: 'CZ', icon: 'fi-cz' },
  { name: 'Democratic Republic Of Congo (Zaire)', code: 'CD', icon: 'fi-cd' },
  { name: 'Denmark', code: 'DK', icon: 'fi-dk' },
  { name: 'Djibouti', code: 'DJ', icon: 'fi-dj' },
  { name: 'Dominica', code: 'DM', icon: 'fi-dm' },
  { name: 'Dominican Republic', code: 'DO', icon: 'fi-do' },
  { name: 'Ecuador', code: 'EC', icon: 'fi-ec' },
  { name: 'Egypt', code: 'EG', icon: 'fi-eg' },
  { name: 'El Salvador', code: 'SV', icon: 'fi-sv' },
  { name: 'Equatorial Guinea', code: 'GQ', icon: 'fi-gq' },
  { name: 'Eritrea', code: 'ER', icon: 'fi-er' },
  { name: 'Estonia', code: 'EE', icon: 'fi-ee' },
  { name: 'Ethiopia', code: 'ET', icon: 'fi-et' },
  { name: 'Fiji', code: 'FJ', icon: 'fi-fj' },
  { name: 'Finland', code: 'FI', icon: 'fi-fi' },
  { name: 'France', code: 'FR', icon: 'fi-fr' },
  { name: 'Gabon', code: 'GA', icon: 'fi-ga' },
  { name: 'Gambia', code: 'GM', icon: 'fi-gm' },
  { name: 'Georgia', code: 'GE', icon: 'fi-ge' },
  { name: 'Germany', code: 'DE', icon: 'fi-de' },
  { name: 'Ghana', code: 'GH', icon: 'fi-gh' },
  { name: 'Great Britain', code: 'GB', icon: 'fi-gb' },
  { name: 'Greece', code: 'GR', icon: 'fi-gr' },
  { name: 'Grenada', code: 'GD', icon: 'fi-gd' },
  { name: 'Guatemala', code: 'GT', icon: 'fi-gt' },
  { name: 'Guinea', code: 'GN', icon: 'fi-gn' },
  { name: 'Guinea-Bissau', code: 'GW', icon: 'fi-gw' },
  { name: 'Guyana', code: 'GY', icon: 'fi-gy' },
  { name: 'Haiti', code: 'HT', icon: 'fi-ht' },
  { name: 'Honduras', code: 'HN', icon: 'fi-hn' },
  { name: 'Hungary', code: 'HU', icon: 'fi-hu' },
  { name: 'Iceland', code: 'IS', icon: 'fi-is' },
  { name: 'India', code: 'IN', icon: 'fi-in' },
  { name: 'Indonesia', code: 'ID', icon: 'fi-id' },
  { name: 'Ireland', code: 'IE', icon: 'fi-ie' },
  { name: 'Israel', code: 'IL', icon: 'fi-il' },
  { name: 'Italy', code: 'IT', icon: 'fi-it' },
  { name: 'Jamaica', code: 'JM', icon: 'fi-jm' },
  { name: 'Japan', code: 'JP', icon: 'fi-jp' },
  { name: 'Jordan', code: 'JO', icon: 'fi-jo' },
  { name: 'Kazakhstan', code: 'KZ', icon: 'fi-kz' },
  { name: 'Kenya', code: 'KE', icon: 'fi-ke' },
  { name: 'Kiribati', code: 'KI', icon: 'fi-ki' },
  { name: 'Kuwait', code: 'KW', icon: 'fi-kw' },
  { name: 'Kyrgyzstan', code: 'KG', icon: 'fi-kg' },
  { name: 'Laos', code: 'LA', icon: 'fi-la' },
  { name: 'Latvia', code: 'LV', icon: 'fi-lv' },
  { name: 'Lesotho', code: 'LS', icon: 'fi-ls' },
  { name: 'Liberia', code: 'LR', icon: 'fi-lr' },
  { name: 'Libya', code: 'LY', icon: 'fi-ly' },
  { name: 'Liechtenstein', code: 'LI', icon: 'fi-li' },
  { name: 'Lithuania', code: 'LT', icon: 'fi-lt' },
  { name: 'Luxembourg', code: 'LU', icon: 'fi-lu' },
  { name: 'Macedonia', code: 'MK', icon: 'fi-mk' },
  { name: 'Madagascar', code: 'MG', icon: 'fi-mg' },
  { name: 'Malawi', code: 'MW', icon: 'fi-mw' },
  { name: 'Malaysia', code: 'MY', icon: 'fi-my' },
  { name: 'Maldives', code: 'MV', icon: 'fi-mv' },
  { name: 'Mali', code: 'ML', icon: 'fi-ml' },
  { name: 'Malta', code: 'MT', icon: 'fi-mt' },
  { name: 'Marshall Islands', code: 'MH', icon: 'fi-mh' },
  { name: 'Mauritania', code: 'MR', icon: 'fi-mr' },
  { name: 'Mauritius', code: 'MU', icon: 'fi-mu' },
  { name: 'Mexico', code: 'MX', icon: 'fi-mx' },
  { name: 'Micronesia', code: 'FM', icon: 'fi-fm' },
  { name: 'Moldova', code: 'MD', icon: 'fi-md' },
  { name: 'Monaco', code: 'MC', icon: 'fi-mc' },
  { name: 'Mongolia', code: 'MN', icon: 'fi-mn' },
  { name: 'Montenegro', code: 'ME', icon: 'fi-me' },
  { name: 'Morocco', code: 'MA', icon: 'fi-ma' },
  { name: 'Mozambique', code: 'MZ', icon: 'fi-mz' },
  { name: 'Myanmar (Burma)', code: 'MM', icon: 'fi-mm' },
  { name: 'Namibia', code: 'NA', icon: 'fi-na' },
  { name: 'Nauru', code: 'NR', icon: 'fi-nr' },
  { name: 'Nepal', code: 'NP', icon: 'fi-np' },
  { name: 'Netherlands', code: 'NL', icon: 'fi-nl' },
  { name: 'New Zealand', code: 'NZ', icon: 'fi-nz' },
  { name: 'Nicaragua', code: 'NI', icon: 'fi-ni' },
  { name: 'Niger', code: 'NE', icon: 'fi-ne' },
  { name: 'Nigeria', code: 'NG', icon: 'fi-ng' },
  { name: 'Norway', code: 'NO', icon: 'fi-no' },
  { name: 'Oman', code: 'OM', icon: 'fi-om' },
  { name: 'Pakistan', code: 'PK', icon: 'fi-pk' },
  { name: 'Palau', code: 'PW', icon: 'fi-pw' },
  { name: 'Panama', code: 'PA', icon: 'fi-pa' },
  { name: 'Papua New Guinea', code: 'PG', icon: 'fi-pg' },
  { name: 'Paraguay', code: 'PY', icon: 'fi-py' },
  { name: 'Peru', code: 'PE', icon: 'fi-pe' },
  { name: 'Philippines', code: 'PH', icon: 'fi-ph' },
  { name: 'Poland', code: 'PL', icon: 'fi-pl' },
  { name: 'Portugal', code: 'PT', icon: 'fi-pt' },
  { name: 'Qatar', code: 'QA', icon: 'fi-qa' },
  { name: 'Romania', code: 'RO', icon: 'fi-ro' },
  { name: 'Rwanda', code: 'RW', icon: 'fi-rw' },
  { name: 'Saint Kitts And Nevis', code: 'KN', icon: 'fi-kn' },
  { name: 'Saint Lucia', code: 'LC', icon: 'fi-lc' },
  { name: 'Saint Vincent And The Grenadines', code: 'VC', icon: 'fi-vc' },
  { name: 'San Marino', code: 'SM', icon: 'fi-sm' },
  { name: 'Sao Tome And Principe', code: 'ST', icon: 'fi-st' },
  { name: 'Saudi Arabia', code: 'SA', icon: 'fi-sa' },
  { name: 'Senegal', code: 'SN', icon: 'fi-sn' },
  { name: 'Serbia', code: 'RS', icon: 'fi-rs' },
  { name: 'Seychelles', code: 'SC', icon: 'fi-sc' },
  { name: 'Sierra Leone', code: 'SL', icon: 'fi-sl' },
  { name: 'Singapore', code: 'SG', icon: 'fi-sg' },
  { name: 'Slovak Republic', code: 'SK', icon: 'fi-sk' },
  { name: 'Slovenia', code: 'SI', icon: 'fi-si' },
  { name: 'Solomon Islands', code: 'SB', icon: 'fi-sb' },
  { name: 'Somalia', code: 'SO', icon: 'fi-so' },
  { name: 'South Africa', code: 'ZA', icon: 'fi-za' },
  { name: 'South Korea', code: 'KR', icon: 'fi-kr' },
  { name: 'South Sudan', code: 'SS', icon: 'fi-ss' },
  { name: 'Spain', code: 'ES', icon: 'fi-es' },
  { name: 'Sri Lanka', code: 'LK', icon: 'fi-lk' },
  { name: 'Suriname', code: 'SR', icon: 'fi-sr' },
  { name: 'Swaziland', code: 'SZ', icon: 'fi-sz' },
  { name: 'Sweden', code: 'SE', icon: 'fi-se' },
  { name: 'Switzerland', code: 'CH', icon: 'fi-ch' },
  { name: 'Taiwan', code: 'TW', icon: 'fi-tw' },
  { name: 'Tajikistan', code: 'TJ', icon: 'fi-tj' },
  { name: 'Tanzania', code: 'TZ', icon: 'fi-tz' },
  { name: 'Thailand', code: 'TH', icon: 'fi-th' },
  { name: 'Timor-Leste', code: 'TL', icon: 'fi-tl' },
  { name: 'Togo', code: 'TG', icon: 'fi-tg' },
  { name: 'Tonga', code: 'TO', icon: 'fi-to' },
  { name: 'Trinidad And Tobago', code: 'TT', icon: 'fi-tt' },
  { name: 'Tunisia', code: 'TN', icon: 'fi-tn' },
  { name: 'Turkey', code: 'TR', icon: 'fi-tr' },
  { name: 'Turkmenistan', code: 'TM', icon: 'fi-tm' },
  { name: 'Tuvalu', code: 'TV', icon: 'fi-tv' },
  { name: 'Uganda', code: 'UG', icon: 'fi-ug' },
  { name: 'Ukraine', code: 'UA', icon: 'fi-ua' },
  { name: 'United Arab Emirates', code: 'AE', icon: 'fi-ae' },
  { name: 'United States', code: 'US', icon: 'fi-us' },
  { name: 'Uruguay', code: 'UY', icon: 'fi-uy' },
  { name: 'Uzbekistan', code: 'UZ', icon: 'fi-uz' },
  { name: 'Vanuatu', code: 'VU', icon: 'fi-vu' },
  { name: 'Vatican City (Holy See)', code: 'VA', icon: 'fi-va' },
  { name: 'Vietnam', code: 'VN', icon: 'fi-vn' },
  { name: 'Western Samoa', code: 'WS', icon: 'fi-ws' },
  { name: 'Yemen', code: 'YE', icon: 'fi-ye' },
  { name: 'Zambia', code: 'ZM', icon: 'fi-zm' },
  { name: 'Zimbabwe', code: 'ZW', icon: 'fi-zw' },
];

export const canShowModel = (model: string, features: string[]): boolean => {
  if (features.length === 0) {
    return false;
  }

  switch (model) {
    case 'GPT':
      return (
        features.includes('GPT') ||
        features.includes('GPT_API') ||
        features.includes('GPT_API_BROWSER') ||
        features.includes('GPT_SEARCH_BROWSER')
      );

    case 'CLAUDE':
      return features.includes('CLAUDE_API');

    case 'DEEP_SEEK':
      return features.includes('DEEP_SEEK_API');

    case 'GEMINI':
      return (
        features.includes('GEMINI_API') ||
        features.includes('GEMINI_SEARCH_BROWSER') ||
        features.includes('GEMINI_BROWSER_API')
      );

    case 'PERPLEXITY':
      return features.includes('PERPLEXITY_API') || features.includes('PERPLEXITY_SEARCH_BROWSER');

    case 'GROK':
      return features.includes('GROK_SEARCH_BROWSER') || features.includes('GROK_API');

    case 'COPILOT':
      return features.includes('COPILOT_SEARCH_BROWSER');

    case 'AI_OVERVIEW':
    case 'AI_MODE':
      return features.includes('AI_OVERVIEW') || features.includes('AI_MODE');

    case 'AI_OVERVIEW_NEW':
      return features.includes('AI_OVERVIEW_NEW');

    default:
      return false;
  }
};
